// OpsOverviewPage — the Ops home (pen Q67 Overview), pure.
//
// Three health tiles in a row (the app, the assistant today, the cost) and
// the newest feedback that is still open. Each part waits and fails alone
// (SF-11): a tile still waiting shows its waiting shape, a read that failed
// shows that part's failure, never a blank. A tile turns red only for an
// honest failure the operator can act on (the app answers but cannot read
// its data). Every detail is one click away on its own page.

export { OpsOverviewPage };

import type { AiUsageSummary } from "@bilda/app/agentCallAdmin";
import type {
    FeedbackAdminList,
    FeedbackAdminReport,
} from "@bilda/app/feedbackAdmin";
import { exhaustSwitchCase, type Remote } from "@bilda/utils";
import { useLingui } from "@lingui/react/macro";
import { type ReactNode, useState } from "react";
import { formatMoney } from "../../app/aiUsage/format.js";
import type { ExternalError } from "../../app/externalError.js";
import type { AppHealth } from "../../app/ops/api.js";
import { FEEDBACK_PATH, feedbackReportHref } from "../../app/ops/route.js";
import { Icon, type IconName } from "../Icon/Icon.js";
import { Skeleton } from "../Skeleton/Skeleton.js";
import {
    ContextChips,
    filterByKind,
    KindChips,
    type KindFilter,
    KindTag,
    latest,
    useFrom,
} from "./feedbackParts.js";
import {
    type OpsAccess,
    type OpsFrame,
    OpsLoadFailed,
    OpsMenu,
    OpsNoAccess,
} from "./OpsMenu.js";

type Read<T> = Remote.Remote<T, ExternalError>;

type OpsOverviewPageProps = {
    readonly frame: OpsFrame;
    readonly access: OpsAccess;
    readonly health: Read<AppHealth>;
    readonly today: Read<AiUsageSummary>;
    readonly month: Read<AiUsageSummary>;
    readonly feedback: Read<FeedbackAdminList>;
    // "See failed calls": AI usage › Calls, today, failed only.
    readonly failedCallsHref: string;
    readonly aiUsageHref: string;
    // "now", for the report times.
    readonly nowMs: number;
    readonly onRetry: () => void;
};

// The newest open reports the Overview lists; the rest is one click away.
const OVERVIEW_REPORTS = 5;

type Dot = "success" | "warning" | "danger";

const Tile = ({
    icon,
    label,
    dot,
    danger,
    value,
    body,
    link,
}: {
    readonly icon: IconName;
    readonly label: string;
    readonly dot?: Dot;
    readonly danger?: true;
    readonly value: ReactNode;
    readonly body: ReactNode;
    readonly link?: { readonly href: string; readonly label: string };
}) => (
    <section
        className={`ops__tile${danger === true ? " ops__tile--danger" : ""}`}
        aria-label={label}
    >
        <span className="ops__tile-label">
            <Icon name={icon} size="sm" />
            {label}
        </span>
        <p className="ops__tile-value">
            {dot === undefined ? null : (
                <span className={`ops__dot--${dot}`}>
                    <Icon name="dot" size="sm" />
                </span>
            )}
            {value}
        </p>
        <p className="ops__tile-body">{body}</p>
        {link === undefined ? null : (
            <a className="ops__more" href={link.href}>
                {link.label}
                <Icon name="arrow-right" size="xs" />
            </a>
        )}
    </section>
);

const Waiting = ({ icon, label }: { icon: IconName; label: string }) => (
    <section className="ops__tile" aria-label={label} aria-busy="true">
        <span className="ops__tile-label">
            <Icon name={icon} size="sm" />
            {label}
        </span>
        <Skeleton variant="block" height="var(--space-6)" width="60%" />
        <Skeleton variant="text" width="90%" />
    </section>
);

const Failed = ({
    icon,
    label,
    onRetry,
}: {
    icon: IconName;
    label: string;
    onRetry: () => void;
}) => {
    const { t } = useLingui();
    return (
        <Tile
            icon={icon}
            label={label}
            value={t`Could not be read`}
            body={
                <button
                    type="button"
                    className="ai-usage__link"
                    onClick={onRetry}
                >
                    {t`Try again`}
                </button>
            }
        />
    );
};

const AppTile = ({
    health,
}: {
    readonly health: OpsOverviewPageProps["health"];
}) => {
    const { t } = useLingui();
    const label = t`App`;
    if (health._tag !== "Remote.Loaded")
        return <Waiting icon="ops" label={label} />;
    switch (health.value) {
        case "ready":
            return (
                <Tile
                    icon="ops"
                    label={label}
                    dot="success"
                    value={t`Up and ready`}
                    body={t`Answers and reads its data. Checked a moment ago.`}
                />
            );
        case "not_ready":
            return (
                <Tile
                    icon="ops"
                    label={label}
                    dot="danger"
                    danger
                    value={t`Running, not ready`}
                    body={t`It answers but cannot read its data, so testers see an error.`}
                />
            );
        case "down":
            return (
                <Tile
                    icon="ops"
                    label={label}
                    dot="danger"
                    danger
                    value={t`Not answering`}
                    body={t`The app did not answer its health check. Testers cannot use it.`}
                />
            );
        default:
            return exhaustSwitchCase(health.value);
    }
};

const AssistantTile = ({
    today,
    failedCallsHref,
    onRetry,
}: {
    readonly today: Read<AiUsageSummary>;
    readonly failedCallsHref: string;
    readonly onRetry: () => void;
}) => {
    const { t } = useLingui();
    const label = t`Assistant`;
    if (today._tag === "Remote.Failed")
        return <Failed icon="sparkle" label={label} onRetry={onRetry} />;
    if (today._tag !== "Remote.Loaded")
        return <Waiting icon="sparkle" label={label} />;
    const { calls, failed } = today.value.totals;
    if (calls === 0)
        return (
            <Tile
                icon="sparkle"
                label={label}
                value={t`No calls today`}
                body={t`Calls show up here as soon as someone uses the assistant.`}
            />
        );
    return (
        <Tile
            icon="sparkle"
            label={label}
            {...(failed > 0 ? { dot: "warning" } : {})}
            value={t`${calls} calls today, ${failed} failed`}
            body={
                failed > 0
                    ? t`The rest completed. Failed calls are listed with what went wrong.`
                    : t`All of them completed.`
            }
            {...(failed > 0
                ? {
                      link: {
                          href: failedCallsHref,
                          label: t`See failed calls`,
                      },
                  }
                : {})}
        />
    );
};

const CostTile = ({
    today,
    month,
    aiUsageHref,
    onRetry,
}: {
    readonly today: Read<AiUsageSummary>;
    readonly month: Read<AiUsageSummary>;
    readonly aiUsageHref: string;
    readonly onRetry: () => void;
}) => {
    const { t, i18n } = useLingui();
    const label = t`Cost`;
    if (today._tag === "Remote.Failed" || month._tag === "Remote.Failed")
        return <Failed icon="ops-cost" label={label} onRetry={onRetry} />;
    if (today._tag !== "Remote.Loaded" || month._tag !== "Remote.Loaded")
        return <Waiting icon="ops-cost" label={label} />;
    const day = formatMoney(i18n.locale, today.value.totals.costMicroUsd);
    const total = formatMoney(i18n.locale, month.value.totals.costMicroUsd);
    return (
        <Tile
            icon="ops-cost"
            label={label}
            value={t`${day} today`}
            body={t`${total} this month, all AI calls together.`}
            link={{ href: aiUsageHref, label: t`Open AI usage and cost` }}
        />
    );
};

const ReportRow = ({
    report,
    nowMs,
}: {
    readonly report: FeedbackAdminReport;
    readonly nowMs: number;
}) => {
    const { t, i18n } = useLingui();
    const from = useFrom();
    const at = new Date(report.at);
    const sameDay = new Date(nowMs).toDateString() === at.toDateString();
    const time = new Intl.DateTimeFormat(i18n.locale, {
        ...(sameDay ? {} : { dateStyle: "medium" }),
        timeStyle: "short",
    }).format(at);
    return (
        <a className="ops__report" href={feedbackReportHref(report.reportId)}>
            <span>
                <KindTag kind={report.kind} />
            </span>
            <span className="ops__report-main">
                <span className="ops__text ops__text--clamp">
                    {report.text}
                </span>
                <ContextChips context={report.context} />
            </span>
            <span className="ops__report-meta">
                <span
                    className={
                        report.reporterEmail === null ? "ops__faint" : undefined
                    }
                >
                    {from(report)}
                </span>
                <span>
                    {report.mayContact ? t`May be contacted` : t`No contact`}
                </span>
                <span>{time}</span>
            </span>
        </a>
    );
};

const Empty = ({ title, body }: { title: string; body: string }) => (
    <div className="ops__panel ops__empty">
        <Icon name="ops-inbox" size="md" />
        <h3 className="ops__empty-title">{title}</h3>
        <p className="ops__empty-body">{body}</p>
    </div>
);

const NewestFeedback = ({
    feedback,
    nowMs,
    onRetry,
}: {
    readonly feedback: Read<FeedbackAdminList>;
    readonly nowMs: number;
    readonly onRetry: () => void;
}) => {
    const { t } = useLingui();
    const [kind, setKind] = useState<KindFilter>("all");
    const title = <h2 className="ops__section-title">{t`Feedback`}</h2>;
    if (feedback._tag === "Remote.Failed")
        return (
            <section className="ops__section">
                <div className="ops__section-head">{title}</div>
                <OpsLoadFailed
                    title={t`Feedback could not be loaded`}
                    onRetry={onRetry}
                />
            </section>
        );
    const value = latest(feedback);
    if (value === undefined)
        return (
            <section className="ops__section" aria-busy="true">
                <div className="ops__section-head">{title}</div>
                <Skeleton variant="block" height="var(--space-24)" />
            </section>
        );
    const { reports, unreadable } = value;
    const open = reports.filter((r) => r.status === "open");
    const shown = filterByKind(open, kind).slice(0, OVERVIEW_REPORTS);
    return (
        <section className="ops__section">
            <div className="ops__section-head">
                {title}
                {open.length === 0 ? null : (
                    <>
                        <KindChips
                            reports={open}
                            value={kind}
                            onChange={setKind}
                        />
                        <a className="ops__more" href={FEEDBACK_PATH}>
                            {t`Open all feedback`}
                            <Icon name="arrow-right" size="xs" />
                        </a>
                    </>
                )}
            </div>
            {reports.length === 0 ? (
                <Empty
                    title={t`No feedback yet`}
                    body={t`Reports testers send with Give feedback and Report this show up here.`}
                />
            ) : open.length === 0 ? (
                <Empty
                    title={t`Nothing left to look at`}
                    body={t`Every report is handled. Handled reports stay on the Feedback page.`}
                />
            ) : (
                <div className="ops__panel">
                    {shown.map((r) => (
                        <ReportRow key={r.reportId} report={r} nowMs={nowMs} />
                    ))}
                </div>
            )}
            {unreadable > 0 ? (
                <p className="ops__foot" role="status">
                    {t`${unreadable} stored reports could not be read and are not shown.`}
                </p>
            ) : null}
        </section>
    );
};

const OpsOverviewPage = (props: OpsOverviewPageProps) => {
    const { t } = useLingui();
    return (
        <div className="ai-usage">
            <OpsMenu
                frame={props.frame}
                access={props.access}
                current="overview"
            />
            <main className="ai-usage__main">
                {props.access === "denied" ? (
                    <OpsNoAccess />
                ) : (
                    <>
                        <header className="ai-usage__heading">
                            <h1 className="ops__title">{t`Overview`}</h1>
                            <p className="ai-usage__lede">
                                {t`What testers sent, and how the app is doing.`}
                            </p>
                        </header>
                        <div className="ops__section">
                            <div className="ops__tiles">
                                <AppTile health={props.health} />
                                <AssistantTile
                                    today={props.today}
                                    failedCallsHref={props.failedCallsHref}
                                    onRetry={props.onRetry}
                                />
                                <CostTile
                                    today={props.today}
                                    month={props.month}
                                    aiUsageHref={props.aiUsageHref}
                                    onRetry={props.onRetry}
                                />
                            </div>
                        </div>
                        <NewestFeedback
                            feedback={props.feedback}
                            nowMs={props.nowMs}
                            onRetry={props.onRetry}
                        />
                    </>
                )}
            </main>
        </div>
    );
};
